import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';

import Swal from 'sweetalert2';
import { EMPTY, firstValueFrom, of, switchMap, tap } from 'rxjs';

import { UnsubscribeOnDestroyAdapter } from '@shared';
import { AuthService } from '@core/http/auth.service';
import { AuthManagementService } from '@core/service/auth-management.service';
import { BusinessGroupService } from '@core/service/business-group.service';
import { UserService } from '@core/http/table-data-services/User.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';

import { AuthLoginRequest } from '@core/models/Http/Request/AuthLoginRequest';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { LoginPayload } from '@core/models/Http/Payloads';

import { decryptLoginPayload, decryptPayload, decryptData } from '@shared/utils/decrypt';
import { encryptData } from '@shared/utils/encrypt';

import { environment } from '@enviroment/environment';
import { UserRole } from '@core/models/Enums/UserRole';

import { WelcomeSplashService } from '@shared/components/welcome-splash/welcome-splash.service';
import { GuidedTourService } from '@core/service/guided-tour.service';
import { TwoFactorService } from '@core/service/two-factor.service';
import { TwoFaDialogComponent } from '@shared/components/two-fa-dialog/two-fa-dialog.component';
import {User} from "@core/models/User";
import { PrivacyPoliciesConsentComponent } from '@shared/components/privacy-policies-consent/privacy-policies-consent.component';
import { PoliticaPrivacidadAceptacion } from '@core/models/Registration';

type EmailNotVerifiedData = { email?: string | null };

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgIf,
    PrivacyPoliciesConsentComponent,
  ],
})
export class SigninComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  @ViewChild(PrivacyPoliciesConsentComponent)
  pendingPoliciesConsent?: PrivacyPoliciesConsentComponent;

  error = '';
  hide = true;
  isEmpleado = false;
  isDoctor = false;
  isParticular = false;
  loginPolicyTypes: string[] = [];
  loginPolicyProfileType: 'MEDICO' | 'PACIENTE' = 'PACIENTE';
  loginPolicyAcceptances: PoliticaPrivacidadAceptacion[] = [];
  acceptingLoginPolicies = false;
  private pendingLoginPolicies: PendingLoginPolicy[] = [];

  user: AuthLoginRequest;
  loginpayload: LoginPayload = {} as LoginPayload;
  userName = '';

  private redirectTo: string | null = null;

  constructor(
    private authService: AuthService,
    private authManagement: AuthManagementService,
    private businessGroupService: BusinessGroupService,
    private userService: UserService,
    private router: Router,
    private httpMsgHandler: HttpMesageHandlerService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private welcomeSplash: WelcomeSplashService,
    private guidedTour: GuidedTourService,
    private twoFactor: TwoFactorService,
    private dialog: MatDialog,
  ) {
    super();
    this.user = new AuthLoginRequest();
  }

  ngOnInit() {
    this.redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');
    if (this.redirectTo) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }

    this.subscribeToUser();
  }

  login() {
    if (this.acceptingLoginPolicies) return;
    if (this.loginPolicyTypes.length > 0) {
      void this.showPendingPoliciesNotice();
      return;
    }
    if (!this.user.usr || !this.user.password) return;

    this.authService
      .login(this.user)
      .pipe(
        switchMap((res: DefaultResponse<any>) => {
          if (res?.code === 10050) {
            const email = this.extractEmailFromNotVerifiedResponse(res);
            void this.showEmailNotVerifiedModal(email);
            return EMPTY;
          }
          if (res?.code === 22009) {
            void this.showPasswordPolicyUpdateModal();
            return EMPTY;
          }
          if (res?.code === 970005) {
            this.handlePendingPoliciesResponse(res);
            return EMPTY;
          }
          return of(res as DefaultResponse<string>);
        }),

        tap((res: DefaultResponse<string>) => {
          this.loginpayload = decryptLoginPayload(res.data, environment.encryptionKey);

          const userDetails = JSON.parse(
            decryptPayload(this.loginpayload.user, environment.encryptionKey)
          );

          const gemp = userDetails.IdGEMP ?? userDetails.GEMP ?? null;

          const safeUser = {
            unique_name:
              userDetails.unique_name ||
              `${userDetails.Nombres ?? ''} ${userDetails.PrimerApellido ?? ''} ${userDetails.SegundoApellido ?? ''}`.trim(),
            role: userDetails.role ?? userDetails.Role ?? userDetails.TipoUsuario ?? '',
            IdUsuario: userDetails.IdUsuario,
            IdTipoUsuario: userDetails.IdTipoUsuario,
            Usr: userDetails.Usr,
            TipoUsuario: userDetails.TipoUsuario,

            IdGEMP: gemp,
            Empresa: userDetails.Empresa,
            IdSucursal: userDetails.IdSucursal,
            NombreSucursal: userDetails.NombreSucursal,

            Nombres: userDetails.Nombres,
            PrimerApellido: userDetails.PrimerApellido,
            SegundoApellido: userDetails.SegundoApellido,

            Imagen: userDetails.Imagen ?? null,
          };

          this.authManagement.saveUserData(
            encryptData(JSON.stringify(safeUser), environment.encryptionKey)
          );

          this.authManagement.saveUserKey(
            `${this.loginpayload.token.iv}:${this.loginpayload.token.cipherText}`
          );

          this.userService.setUserName(
            `${(userDetails.Nombres ?? '').split(' ')[0] ?? ''} ${userDetails.PrimerApellido ?? ''}`.trim()
          );

          if (gemp) {
            this.businessGroupService.emitBusinessGroupId(gemp, true);
          }
        })
      )
      .subscribe({
        next: async () => {
          const userDetails = JSON.parse(
            decryptPayload(this.loginpayload.user, environment.encryptionKey)
          );

          const gemp = userDetails.IdGEMP ?? userDetails.GEMP ?? null;

          this.isEmpleado = this.authManagement.isUserPharmacyEmployee();
          this.isDoctor = this.authManagement.isUserDoctor();
          this.isParticular = this.authManagement.isUserParticular();

          const needs2FAFirst =
            this.authManagement.isUserDoctor() || this.authManagement.isUserParticular();

          this.cdr.detectChanges();

          const target = this.redirectTo || this.authManagement.getDefaultRoute();

          if (needs2FAFirst && this.twoFactor.mustEnforce2FA()) {
            const ok = await this.twoFactor.ensure2FA(async (sendFn) => {
              await sendFn();

              const ref = this.dialog.open(TwoFaDialogComponent, {
                disableClose: true,
                autoFocus: true,
              });

              const code = await firstValueFrom(ref.afterClosed());
              return (code ?? null) as string | null;
            });

            if (!ok) {
              this.authManagement.hardLocalLogout();
              return;
            }
          }

          if (gemp) {
            await firstValueFrom(
              this.businessGroupService.changeGempAndRefreshToken(gemp)
            );
          }

          await this.welcomeSplash.showAndNavigate(
            () => this.router.navigateByUrl(target, { replaceUrl: true }),
            {
              userName: this.userName,
              logoSrc: 'assets/images/logo1.png',
              fadeInMs: 800,
              stayMs: 1100,
              fadeOutMs: 800,
            }
          );
        },
        error: (err) => this.httpMsgHandler.handleMsg(err),
      });
  }

  private subscribeToUser() {
    this.userService.userName$.subscribe((name) => {
      this.userName = name!;
    });
  }

  async continueAfterPolicyAcceptance(): Promise<void> {
    if (this.acceptingLoginPolicies) {
      return;
    }

    const consentAccepted = await this.pendingPoliciesConsent?.ensureAccepted();
    if (!consentAccepted) {
      return;
    }

    const policiesToAccept = this.pendingLoginPolicies
      .filter((item) => item.requiereAceptacion && !item.aceptado)
      .filter((item) => item.tipo !== 'COOKIES');

    if (!policiesToAccept.length) {
      return;
    }

    if (policiesToAccept.some((item) => !item.idUsuario || !item.tipo)) {
      await Swal.fire({
        icon: 'error',
        title: 'NO SE PUDIERON ACEPTAR LAS POLITICAS',
        text: 'FALTA INFORMACION PARA REGISTRAR LA ACEPTACION. INTENTA DE NUEVO MAS TARDE.',
      });
      return;
    }

    this.acceptingLoginPolicies = true;
    void this.showAcceptingPoliciesModal();
    try {
      const sessionId = this.authService.getOrCreateLoginPoliciesSessionId();

      for (let index = 0; index < policiesToAccept.length; index++) {
        if (index > 0) {
          await this.wait(3000);
        }

        const policy = policiesToAccept[index];
        await firstValueFrom(
          this.authService.acceptPendingLoginPolicy({
            idUsuario: policy.idUsuario,
            tipo: policy.tipo,
            canal: 'WEB',
            sessionId,
            deviceId: null,
          })
        );
      }

      this.acceptingLoginPolicies = false;
      Swal.close();
      this.loginPolicyTypes = [];
      this.loginPolicyAcceptances = [];
      this.pendingLoginPolicies = [];
      this.login();
    } catch (err) {
        this.acceptingLoginPolicies = false;
        Swal.close();
        this.httpMsgHandler.handleMsg(err as any);
    }
  }

  private async showAcceptingPoliciesModal(): Promise<void> {
    await Swal.fire({
      title: 'ACEPTANDO POLITICAS DE PRIVACIDAD',
      html: `
        <p>ESTAMOS REGISTRANDO TUS ACEPTACIONES.</p>
        <p>ESPERA UN MOMENTO, SERAS REDIRIGIDO AUTOMATICAMENTE AL FINALIZAR.</p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  }

  private handlePendingPoliciesResponse(res: DefaultResponse<any>): void {
    this.pendingLoginPolicies = this.extractPendingPolicies(res.data);
    const types = this.pendingLoginPolicies
      .filter((item) => item.requiereAceptacion && !item.aceptado)
      .map((item) => item.tipo)
      .filter((tipo) => tipo !== 'COOKIES');

    this.loginPolicyTypes = Array.from(new Set(types));
    this.loginPolicyProfileType = this.loginPolicyTypes.includes('POPUP_MEDICO')
      ? 'MEDICO'
      : 'PACIENTE';
    this.loginPolicyAcceptances = [];

    if (!this.loginPolicyTypes.length) {
      this.login();
      return;
    }

    setTimeout(() => void this.showPendingPoliciesNotice());
  }

  private async showPendingPoliciesNotice(): Promise<void> {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'POLITICAS PENDIENTES',
      html: `
        <p>PARA INICIAR SESION ES NECESARIO REVISAR Y ACEPTAR LAS POLITICAS VIGENTES DE LA PLATAFORMA.</p>
        <p>SE ABRIRA EL VISOR DE POLITICAS PARA QUE PUEDAS LEERLAS Y CONFIRMAR TU ACEPTACION.</p>
      `,
      confirmButtonText: 'VER POLITICAS',
      cancelButtonText: 'CERRAR',
      showCancelButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      focusConfirm: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    await this.continueAfterPolicyAcceptance();
  }

  private extractPendingPolicies(data: unknown): PendingLoginPolicy[] {
    try {
      const raw = String(data ?? '').trim();
      if (!raw) return [];

      const decrypted = raw.includes(':')
        ? decryptData(raw, environment.encryptionKey)
        : raw;
      const parsed = JSON.parse(decrypted);
      const items = Array.isArray(parsed) ? parsed : [parsed];

      return items
        .map((item: any) => ({
          idUsuario: String(item?.idUsuario ?? item?.IdUsuario ?? '').trim(),
          tipo: String(item?.tipo ?? item?.Tipo ?? '').toUpperCase(),
          requiereAceptacion: this.isTruthy(item?.requiereAceptacion ?? item?.RequiereAceptacion),
          aceptado: this.isTruthy(item?.aceptado ?? item?.Aceptado),
        }))
        .filter((item) => !!item.tipo);
    } catch {
      return [];
    }
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private wait(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  private extractEmailFromNotVerifiedResponse(res: DefaultResponse<any>): string {
    try {
      const raw = (res?.data ?? '').toString().trim();
      if (!raw) return '';

      // Caso 1: viene encriptado
      if (raw.includes(':')) {
        const decrypted = decryptData(raw, environment.encryptionKey)?.toString().trim();
        if (!decrypted) return '';

        try {
          const parsed = JSON.parse(decrypted);

          // Si al parsear obtenemos un string, ese es el correo correcto
          if (typeof parsed === 'string') {
            const email = parsed.trim();
            if (email.includes('@')) return email;
          }

          if (parsed && typeof parsed === 'object') {
            const email = (parsed.email ?? parsed.Email ?? '').toString().trim();
            if (email.includes('@')) return email;
          }
        } catch {
        }

        // 2) Si no era JSON, limpiar posibles comillas manualmente
        const cleanedDecrypted = decrypted.replace(/^"+|"+$/g, '').trim();
        if (cleanedDecrypted.includes('@')) {
          return cleanedDecrypted;
        }
      }

      // Caso 2: por alguna razón ya vino texto plano
      const cleanedRaw = raw.replace(/^"+|"+$/g, '').trim();
      if (cleanedRaw.includes('@')) {
        return cleanedRaw;
      }

      // Fallback con el usuario capturado en el login
      const fallbackUser = (this.user?.usr ?? '').toString().trim().replace(/^"+|"+$/g, '');
      if (fallbackUser.includes('@')) {
        return fallbackUser;
      }

      return '';
    } catch {
      const fallbackUser = (this.user?.usr ?? '').toString().trim().replace(/^"+|"+$/g, '');
      return fallbackUser.includes('@') ? fallbackUser : '';
    }
  }

  private async showEmailNotVerifiedModal(emailFromApi?: string) {
    const email = (emailFromApi ?? '').toString().trim();

    const result = await Swal.fire({
      icon: 'warning',
      title: 'CUENTA NO CONFIRMADA',
      html: `
      <p>DEBES CONFIRMAR TU CUENTA.</p>
      <p>REVISA TU CORREO Y SIGUE LAS INSTRUCCIONES.</p>
      <br/>
    `,
      showCancelButton: true,
      confirmButtonText: 'REENVIAR CORREO',
      cancelButtonText: 'CERRAR',
      focusConfirm: true,
    });

    if (!result.isConfirmed) return;

    this.userService.resendVerifyEmail(email).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'CORREO ENVIADO',
          text: 'REVISA TU BANDEJA (Y SPAM) PARA CONFIRMAR TU CUENTA.',
        });
      },
      error: (err) => this.httpMsgHandler.handleMsg(err),
    });
  }

  private async showPasswordPolicyUpdateModal() {
    await Swal.fire({
      icon: 'info',
      title: 'ACTUALIZAR CONTRASEÑA',
      html: `
      <p>POR NUEVAS POLÍTICAS DE SEGURIDAD SE REQUIERE ACTUALIZAR LA CONTRASEÑA.</p>
      <p>REVISA TU CORREO Y SIGUE LAS INSTRUCCIONES.</p>
    `,
      confirmButtonText: 'ENTENDIDO',
    });
  }
}

interface PendingLoginPolicy {
  idUsuario: string;
  tipo: string;
  requiereAceptacion: boolean;
  aceptado: boolean;
}
